import type { User, UserProfile } from "stoat.js";

/** A profile just fetched isn't fetched again for this long */
const FRESH_MS = 60_000;
/** Animated banners bigger than this only load when the card really opens */
const PRELOAD_MAX_BYTES = 3_000_000;
const MAX_KEPT = 200;

const kept = new Map<string, { at: number; profile: Promise<UserProfile> }>();

/**
 * Konkord: a user's profile, shared between the hover that anticipates the
 * click and the card itself, so the banner doesn't wait for a second request
 */
export function loadProfile(user: User) {
  const entry = kept.get(user.id);
  if (entry && Date.now() - entry.at < FRESH_MS) return entry.profile;
  const profile = user.fetchProfile();
  if (kept.size >= MAX_KEPT) kept.clear();
  kept.set(user.id, { at: Date.now(), profile });
  // a failure isn't kept: the next one asks again
  profile.catch(() => kept.delete(user.id));
  return profile;
}

/** Forget a profile that has just changed */
export function forgetProfile(id: string) {
  kept.delete(id);
}

/**
 * Konkord: the address of the animated banner itself. The usual one ends in
 * "/original", which the file host redirects to the file's name: 250 ms more,
 * every time, for a redirect that isn't kept in its cache.
 */
export function directBannerUrl(profile: UserProfile | undefined) {
  const url = profile?.animatedBannerURL;
  const name = profile?.banner?.filename;
  return url?.endsWith("/original") && name
    ? `${url.slice(0, -"/original".length)}/${encodeURIComponent(name)}`
    : url;
}

function preloadImage(url: string | undefined) {
  if (url) new Image().src = url;
}

/**
 * Konkord: the mouse stopped on someone: their profile and banner are probably
 * next. Files stay in the browser's cache and come from the file host, never
 * through our server.
 */
export function preloadProfile(user: User) {
  void loadProfile(user)
    .then((profile) => {
      preloadImage(profile.bannerURL);
      if ((profile.banner?.size ?? Infinity) <= PRELOAD_MAX_BYTES)
        preloadImage(directBannerUrl(profile));
    })
    .catch(() => {});
}

/** A small preview under the animated banner, while that one loads */
export function bannerPreviewOf(profile: UserProfile | undefined) {
  const preview = profile?.bannerURL;
  return preview && preview !== directBannerUrl(profile) ? preview : undefined;
}
